// @owners { team: patients }
import { type Option } from '@alto/design-system';
import {
  type FertilityCycleMedicationType,
  FertilityCycleMedicationTypeMap,
} from '@alto/scriptdash/alto/fertility_cycles/types/v1/fertility_cycle_medication_type';

const { GONAL_F, MENOPUR, CETROTIDE } = FertilityCycleMedicationTypeMap;

export const TRIGGER_DATE_STRINGS = {
  HEADLINE_UNKNOWN_DATE: 'Do you know your trigger date (aka when this is all done)?',
  SUBHEADLINE_UNKNOWN_DATE:
    'If you don’t know yet, that’s fine — estimate it or add the date of your next doctor’s appointment.',
  HEADLINE_UPCOMING_DATE: (date: string) => `Is your anticipated trigger date still ${date}?`,
  SUBHEADLINE_UPCOMING_DATE: 'If your trigger date has changed, please update it below.',
  HEADLINE_PAST_DATE: (date: string) => `Was your trigger date ${date}?`,
  SUBHEADLINE_PAST_DATE: 'If your trigger date changed, please update it below.',
  CLEAR_DATE_LABEL: 'Clear Date',
  MORE_INFO_LABEL: 'Why are you asking for this?',
  MORE_INFO_ACTION_SHEET_TITLE: 'What’s a trigger date?',
  MORE_INFO_ACTION_SHEET_BODY:
    'Your trigger date is the date your clinic has instructed you to inject your trigger shot(s). These shots trigger your body to release eggs and are given approximately 36 hours before your retrieval. If you are uncertain about your trigger shot(s), please reach out to your clinic.',
};

export const QUANTITIES_REVIEW_STRINGS = {
  HEADLINE: 'Your personalized medication order',
  SUBHEADLINE:
    'Based on where you are in your cycle, here are the quantities you will receive for the following medications:',
  BLOCKER_HEADER: 'Assistance Required',
  BLOCKER_EXCEED_SUPPLY: (meds: string, days_max: number) =>
    `The quantity of ${meds || 'medication'} in your cart exceeds ${days_max} days for your current cycle.`,
  BLOCKER_TRIGGER:
    'The trigger date you previously provided has passed or you have enough medication to last you to the provided trigger date, so we’ll need to assist you with your delivery.',
  INFO_HEADER: 'Quantities Increased',
  INFO_WEEKEND_QUANTITY_INCREASE:
    'Since your delivery will be arriving by mail, we’ve increased your quantities to make sure you’re covered through the weekend.',
  INFO_WEEKEND_TRIGGER: (date: string) =>
    `If you’d like to adjust your dispensed quantities to only last until your trigger date${
      date ? ` on ${date}` : ''
    }, `,
  DISCLAIMER: 'You’ll be able to review your full order in checkout.',
  CONTACT_US_BUTTON_TEXT: 'Contact us',
  BACK_BUTTON_TEXT: 'Back to cart',
};

export const BLOCKER_SUPPLY_LIMITS = {
  default: 14,
  cetrotide: 8,
};

export const FULL_MONTH_NO_YEAR_DATE_FORMAT = 'MMMM do';

export const MEDICATION_TYPES_DISPLAY: Partial<Record<FertilityCycleMedicationType, string>> = {
  [GONAL_F]: 'Gonal-f',
  [MENOPUR]: 'Menopur',
  [CETROTIDE]: 'Cetrotide',
  any: '',
};

export const CALCULATION_INFO_STRINGS: Partial<
  Record<FertilityCycleMedicationType, { STEP_1: string; STEP_2: string; STEP_3: string; EXAMPLE: string }>
> = {
  [GONAL_F]: {
    STEP_1:
      'If you have Gonal-f pens on hand, determine the amount you have left by measuring the number of IUs remaining in the pens. If you have vials, count the number of unopened vials and multiply that number by the size of the vial',
    STEP_2: 'Divide the total amount on hand by your current prescribed dosage per day',
    STEP_3: 'Round down to the nearest whole day',
    EXAMPLE:
      '450 IU remaining on hand / dosage of 250 IU per day = 1.8, which rounds down to 1 day of medication on hand',
  },
  [MENOPUR]: {
    STEP_1:
      'Count the number of Menopur powder vials you have on hand and multiple that number by 75 IU (1 vial = 75 IU)',
    STEP_2: 'Divide the total amount on hand by your current prescribed dosage per day',
    STEP_3: 'Round down to the nearest whole day',
    EXAMPLE: '450 IU remaining on hand / dosage of 150 IU (2 vials) per day = 3 days of medication on hand',
  },
  [CETROTIDE]: {
    STEP_1: 'Count the number of Cetrotide kits you have on hand',
    STEP_2:
      'Divide the total amount on hand by your current prescribed dosage per day (this may be whole kits or a partial kit)',
    STEP_3: 'Round down to the nearest whole day',
    EXAMPLE: '2 kits on hand / dosage of 1 kit per day = 2 days of medication on hand',
  },
  any: {
    STEP_1: '',
    STEP_2: '',
    STEP_3: '',
    EXAMPLE: '',
  },
};

export const DAYS_SUPPLY_OPTIONS: Option[] = [
  {
    label: '0 days worth',
    value: '0',
  },
  {
    label: '1 days worth',
    value: '1',
  },
  {
    label: '2 days worth',
    value: '2',
  },
  {
    label: '3 days worth',
    value: '3',
  },
  {
    label: '4 days worth',
    value: '4',
  },
  {
    label: '5 days worth',
    value: '5',
  },
  {
    label: '6 days worth',
    value: '6',
  },
  {
    label: '7+ days worth',
    value: '7',
  },
];

export const CETROTIDE_FIRST_FILL_DAYS_SUPPLY_OPTIONS: Option[] = [
  {
    label: '0 days worth',
    value: '0',
  },
  {
    label: '1 days worth',
    value: '1',
  },
  {
    label: '2 days worth',
    value: '2',
  },
  {
    label: '3 days worth',
    value: '3',
  },
  {
    label: '4+ days worth',
    value: '4',
  },
];
